import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { Box } from '@vio/atlas/helpers/Box';
import { Text } from '@vio/atlas/Text';
import { offerAccessMessages } from 'components/Offer/Shared/messages';
import { RoomLabels, RoomLabelsText } from 'components/Offer/Shared/RoomLabels';
import { YourChoiceLabel } from 'components/Offer/Shared/YourChoiceLabel';
import { SplitBookingBundleType } from 'components/Offer/types';
import { getOfferAccessFlowSettings } from 'modules/sapiSearch/selectors';
import { Hotel } from 'modules/sapiSearch/slice';
import { toggle } from 'opticks';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { OfferSecondaryLayoutVariant } from '@daedalus/core/src/offer/types/OfferLayout';
import { SearchOffer } from '@daedalus/core/src/offer/types/SearchOffer';
interface MainProps {
  hasRoomLabels?: boolean;
  showSignInToUnlock: boolean;
  variant: OfferSecondaryLayoutVariant;
  offer: SearchOffer;
  isSplitBooking?: false;
  splitBookingType?: never;
  isYourChoice?: never;
  savingsMessage?: never;
  hotelId: Hotel['objectID'];
  isAppLockedDeal?: boolean;
}
interface SplitBookingProps {
  isSplitBooking: true;
  variant: OfferSecondaryLayoutVariant;
  splitBookingType: SplitBookingBundleType;
  isYourChoice: boolean;
  savingsMessage: string;
  hasRoomLabels?: never;
  showSignInToUnlock?: never;
  offer?: never;
  hotelId?: never;
  isAppLockedDeal?: never;
}
const _exp = /*#__PURE__*/() => Text;
const SubTitle = /*#__PURE__*/styled(_exp())({
  name: "SubTitle",
  class: "s11wifae",
  propsAsIs: true
});
const getOfferAccessFlowLabel = ({
  isAppLocked,
  isAuthLocked,
  isSMSLocked
}: {
  isAppLocked: boolean;
  isAuthLocked: boolean;
  isSMSLocked: boolean;
}) => {
  if (isAppLocked) return offerAccessMessages.appLocked;
  if (isAuthLocked) return offerAccessMessages.signInToBookLabel;
  if (isSMSLocked) return offerAccessMessages.smsConfirmation;
  return null;
};
export const RoomLabelsComponent: React.FC<MainProps | SplitBookingProps> = ({
  hasRoomLabels = true,
  variant = 'SecondaryDefault',
  showSignInToUnlock,
  offer,
  isSplitBooking,
  splitBookingType,
  isYourChoice = false,
  savingsMessage = '',
  hotelId,
  isAppLockedDeal
}) => {
  // Added as part of 7d6464d8-move-auth-to-ap
  const isMoveAuthToAPEnabled = toggle('7d6464d8-move-auth-to-ap', false, true);
  const {
    formatMessage
  } = useIntl();
  const {
    isAppRequiredUnlocked,
    isAuthRequiredUnlocked,
    isSMSRequiredUnlocked
  } = useSelector(state => getOfferAccessFlowSettings(state, hotelId, offer?.id));
  const offerAccessFlowLabel = getOfferAccessFlowLabel({
    isAppLocked: isAppRequiredUnlocked,
    isAuthLocked: isAuthRequiredUnlocked,
    isSMSLocked: isSMSRequiredUnlocked
  });
  const translatedOfferAccessFlowLabel = offerAccessFlowLabel ? formatMessage(offerAccessFlowLabel) : null;

  // Added as a part of 70766997-desktop-hotel-card-secondary-offers
  if (variant === 'SecondaryInline' && hasRoomLabels) return <RoomLabelsText offer={offer} translatedOfferAccessFlowLabel={isMoveAuthToAPEnabled ? translatedOfferAccessFlowLabel : null} />;
  if (isSplitBooking) {
    return <Box display="flex" gap="s200" flexWrap="wrap">
        {isYourChoice && <Box display="flex" whiteSpace="nowrap" gap="s200">
            <YourChoiceLabel />
            <span>·</span>
          </Box>}
        {splitBookingType === 'single_flow' && variant === 'SecondaryDefault' && <Box whiteSpace="nowrap">
              <Text variant="bodyXS" colorPath="content.neutral.c600">
                <FormattedMessageWrapper id="roomSwitchRequired" defaultMessage="Room switch required" />
              </Text>
            </Box>}
        {splitBookingType === 'split_flow' && <Box whiteSpace="nowrap">
            <SubTitle variant="bodyXS" colorPath="content.neutral.c600">
              {savingsMessage}
            </SubTitle>
          </Box>}
      </Box>;
  }
  if (isAppLockedDeal) return <Text variant="bodyXS" colorPath="content.neutral.c600">
        <FormattedMessageWrapper id="downloadAppAccessPrice" defaultMessage="Download our app - access this price" />
      </Text>;
  if (showSignInToUnlock) return <Text variant="bodyXS" colorPath="content.special.c600">
        <FormattedMessageWrapper id="signInToUnlockLabel" defaultMessage="Sign in to unlock" />
      </Text>;
  return <>
      {hasRoomLabels && <RoomLabels isAlternativeColor offer={offer} hotelId={hotelId} />}
      {isMoveAuthToAPEnabled && translatedOfferAccessFlowLabel && <Text variant="bodyXS" colorPath="content.neutral.c600">
          {translatedOfferAccessFlowLabel}
        </Text>}
    </>;
};

require("./RoomLabelsComponent.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./RoomLabelsComponent.tsx");